// Exam Online renders only a small subset of HTML for question/option fields.
// Tags it does not support (e.g. <ul>/<ol>/<li> and other block/structural tags)
// end up displayed literally on the test-taking platform. This helper converts
// the AMS rich-text HTML into an Exam Online friendly representation:
//   - list items become bullet lines separated by <br/>
//   - <sup>/<sub> become Unicode equivalents (for Math/Physics/Chemistry)
//   - <p>/<div> blocks are separated with <br/>
//   - <img> and <br/> are preserved (Exam Online renders these)
//   - basic inline formatting (<b>/<strong>/<i>/<em>/<u>) is preserved
//   - any other tag is stripped but its text content is kept

const SUPERSCRIPT_MAP: { [key: string]: string } = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '+': '⁺',
  '-': '⁻',
  '=': '⁼',
  '(': '⁽',
  ')': '⁾',
  n: 'ⁿ',
  i: 'ⁱ',
}

const SUBSCRIPT_MAP: { [key: string]: string } = {
  '0': '₀',
  '1': '₁',
  '2': '₂',
  '3': '₃',
  '4': '₄',
  '5': '₅',
  '6': '₆',
  '7': '₇',
  '8': '₈',
  '9': '₉',
  '+': '₊',
  '-': '₋',
  '=': '₌',
  '(': '₍',
  ')': '₎',
}

const INLINE_KEEP_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U'])

const toUnicode = (text: string, map: { [key: string]: string }) =>
  text
    .split('')
    .map(char => map[char] ?? char)
    .join('')

const serializeNode = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || ''
  if (node.nodeType !== Node.ELEMENT_NODE) return ''

  const element = node as HTMLElement
  const tag = element.tagName

  if (tag === 'IMG') return element.outerHTML
  if (tag === 'BR') return '<br/>'

  const inner = Array.from(element.childNodes).map(serializeNode).join('')

  switch (tag) {
    case 'SUP':
      return toUnicode(inner, SUPERSCRIPT_MAP)
    case 'SUB':
      return toUnicode(inner, SUBSCRIPT_MAP)
    case 'LI':
      return `• ${inner}<br/>`
    case 'UL':
    case 'OL':
      return inner
    case 'P':
    case 'DIV':
      return `${inner}<br/>`
    default:
      if (INLINE_KEEP_TAGS.has(tag)) {
        const lowerTag = tag.toLowerCase()
        return `<${lowerTag}>${inner}</${lowerTag}>`
      }
      return inner
  }
}

export function formatHtmlForExamOnline(htmlText: any): string {
  if (!htmlText || typeof htmlText !== 'string') return ''

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlText

  let result = Array.from(tempDiv.childNodes).map(serializeNode).join('')

  // Collapse excessive line breaks and trim trailing ones
  result = result
    .replace(/(\s*<br\/>\s*){3,}/g, '<br/><br/>')
    .replace(/(<br\/>)+$/, '')
    .trim()

  return result
}
